import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Field, Form, Formik } from 'formik';

import { flutterAdminApi } from './api';
import { buildFlutterBuilderValues } from './builder';
import type { FlutterBuilderValues } from './types';

type BuilderStep = 1 | 2 | 3 | 4;

export const Route = createFileRoute('/dashboard/feeds/build')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      eventIds: typeof search.eventIds === 'string' ? search.eventIds : '',
      leagueCode: typeof search.leagueCode === 'string' ? search.leagueCode : 'uk',
    };
  },
  component: FlutterBuildPage,
});

function FlutterBuildPage() {
  const navigate = useNavigate();
  const { eventIds, leagueCode } = Route.useSearch();

  const providerEventIds = React.useMemo(
    () =>
      eventIds
        .split(',')
        .map(v => v.trim())
        .filter(Boolean),
    [eventIds]
  );

  const [step, setStep] = React.useState<BuilderStep>(1);
  const [fixtureSearch, setFixtureSearch] = React.useState('');
  const [marketSearch, setMarketSearch] = React.useState('');
  const [openEventIds, setOpenEventIds] = React.useState<string[]>([]);
  const [selectedFixtureIds, setSelectedFixtureIds] = React.useState<string[]>(providerEventIds);

  React.useEffect(() => {
    setSelectedFixtureIds(providerEventIds);
  }, [providerEventIds]);

  const snapshotQ = useQuery({
    queryKey: ['flutter-admin-snapshot', providerEventIds],
    queryFn: () => flutterAdminApi.getCombinedSnapshot(providerEventIds),
    enabled: providerEventIds.length > 0,
  });

  if (!providerEventIds.length) {
    return <div className="p-12">No events selected.</div>;
  }

  if (!leagueCode) {
    return <div className="p-12">No league selected.</div>;
  }

  if (snapshotQ.isLoading) {
    return <div className="p-12">Loading builder…</div>;
  }

  if (snapshotQ.isError) {
    return <div className="p-12">Error: {(snapshotQ.error as Error).message}</div>;
  }

  const snapshot = snapshotQ.data;
  if (!snapshot) {
    return <div className="p-12">No snapshot found.</div>;
  }

  const initialValues = buildFlutterBuilderValues(snapshot);

  return (
    <Formik<FlutterBuilderValues>
      initialValues={initialValues}
      enableReinitialize={false}
      onSubmit={async (values, helpers) => {
        try {
          const selectedQuestions = values.questions
            .filter(question => selectedFixtureIds.includes(question.providerEventId))
            .filter(question => question.enabled)
            .map(question => ({
              questionGroupKey: question.questionGroupKey,
              providerEventId: question.providerEventId,
              providerEventName: question.providerEventName,
              questionText: question.questionText.trim(),
              questionType: question.questionType,
              sourceMarkets: question.sourceMarkets.map(market => ({
                brand: market.brand,
                providerEventId: market.providerEventId,
                providerMarketId: market.providerMarketId,
                providerMarketName: market.providerMarketName,
              })),
              options: question.options
                .filter(option => option.enabled)
                .map(option => ({
                  logicalOptionKey: option.logicalOptionKey,
                  optionText: option.optionText.trim(),
                  offers: option.offers.map(offer => ({
                    brand: offer.brand,
                    providerEventId: offer.providerEventId,
                    providerMarketId: offer.providerMarketId,
                    providerMarketName: offer.providerMarketName,
                    providerSelectionId: offer.providerSelectionId,
                    selectionName: offer.selectionName,
                    oddsDecimal: offer.oddsDecimal ?? null,
                  })),
                }))
                .filter(option => option.optionText)
                .filter(option => option.offers.length > 0),
            }))
            .filter(question => question.questionText)
            .filter(question => question.options.length >= 2);

          if (!values.gameName.trim()) {
            helpers.setStatus({ error: 'Enter a game name.' });
            setStep(1);
            helpers.setSubmitting(false);
            return;
          }

          if (!values.startUtc?.trim()) {
            helpers.setStatus({ error: 'Enter a start date and time.' });
            setStep(1);
            helpers.setSubmitting(false);
            return;
          }

          if (!selectedFixtureIds.length) {
            helpers.setStatus({ error: 'Select at least one fixture.' });
            setStep(2);
            helpers.setSubmitting(false);
            return;
          }

          if (!selectedQuestions.length) {
            helpers.setStatus({
              error: 'Select at least one valid question with at least two enabled options.',
            });
            setStep(3);
            helpers.setSubmitting(false);
            return;
          }

          const result = await flutterAdminApi.createPickem({
            leagueCode,
            gameName: values.gameName.trim(),
            startUtc: values.startUtc,
            questions: selectedQuestions,
          });

          console.log('FLUTTER PICKEM PAYLOAD', JSON.stringify(result, null, 2));

          helpers.setStatus({ success: `Created pickem ${result.pickemId}` });

          await navigate({
            to: '/dashboard/pickems/$id',
            params: { id: result.pickemId },
            search: { league_id: undefined },
          });
        } catch (error) {
          helpers.setStatus({
            error: error instanceof Error ? error.message : 'Failed to create pickem',
          });
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting, status, setStatus, submitForm }) => {
        const allGroupedQuestions = groupQuestionsByEvent(values.questions);

        const activeGroupedQuestions = allGroupedQuestions.filter(group =>
          selectedFixtureIds.includes(group.providerEventId)
        );

        const selectedQuestions = values.questions.filter(
          q => q.enabled && selectedFixtureIds.includes(q.providerEventId)
        );

        const availableEvents = snapshot.events.filter(event =>
          providerEventIds.includes(event.providerEventId)
        );

        const normalizedFixtureSearch = fixtureSearch.trim().toLowerCase();
        const visibleEvents = availableEvents.filter(event => {
          if (!normalizedFixtureSearch) return true;
          return (
            event.name.toLowerCase().includes(normalizedFixtureSearch) ||
            event.providerEventId.toLowerCase().includes(normalizedFixtureSearch)
          );
        });

        const reviewQuestionsByEvent = groupQuestionsByEvent(selectedQuestions);

        const invalidSelectedQuestions = selectedQuestions.filter(question => {
          const enabledOptions = question.options.filter(option => option.enabled);
          return !question.questionText.trim() || enabledOptions.length < 2;
        });

        const canGoStep2 = values.gameName.trim().length > 0 && values.startUtc.trim().length > 0;

        const canGoStep3 = selectedFixtureIds.length > 0;

        const canGoStep4 =
          canGoStep3 && selectedQuestions.length > 0 && invalidSelectedQuestions.length === 0;

        const syncQuestionsWithFixtures = (nextFixtureIds: string[]) => {
          const allowed = new Set(nextFixtureIds);

          values.questions.forEach((question, index) => {
            if (!allowed.has(question.providerEventId) && question.enabled) {
              setFieldValue(`questions.${index}.enabled`, false, false);
            }
          });
        };

        const goToStep = (nextStep: BuilderStep) => {
          setStatus(undefined);

          if (nextStep === 2 && !canGoStep2) {
            setStatus({ error: 'Enter a game name and start date/time first.' });
            setStep(1);
            return;
          }

          if (nextStep === 3 && !canGoStep3) {
            setStatus({ error: 'Select at least one fixture first.' });
            setStep(2);
            return;
          }

          if (nextStep === 4 && !canGoStep4) {
            setStatus({
              error:
                invalidSelectedQuestions.length > 0
                  ? 'Fix invalid selected questions before continuing.'
                  : 'Select at least one valid question first.',
            });
            setStep(3);
            return;
          }

          setStep(nextStep);
        };

        console.log('builder debug', {
          selectedFixtureIds,
          totalQuestions: values.questions.length,
          questionEventIds: Array.from(new Set(values.questions.map(q => q.providerEventId))),
          allGroupedQuestions: allGroupedQuestions.map(g => ({
            providerEventId: g.providerEventId,
            providerEventName: g.providerEventName,
            count: g.questions.length,
          })),
          activeGroupedQuestions: activeGroupedQuestions.map(g => ({
            providerEventId: g.providerEventId,
            providerEventName: g.providerEventName,
            count: g.questions.length,
          })),
        });

        return (
          <Form className="space-y-6 p-6 lg:p-12">
            <header className="space-y-3 rounded-2xl border p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Pickem Builder</div>
                  <h1 className="text-2xl font-semibold">Create a Flutter pickem</h1>
                  <p className="text-sm text-muted-foreground">
                    Build your pickem in four steps: details, fixtures, questions, and review.
                  </p>
                </div>

                <div className="text-sm text-muted-foreground">
                  League: <span className="font-medium text-foreground">{leagueCode}</span>
                </div>
              </div>

              <StepIndicator currentStep={step} />
            </header>

            {status?.error ? (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                {status.error}
              </div>
            ) : null}

            {status?.success ? (
              <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-700">
                {status.success}
              </div>
            ) : null}

            {step === 1 ? (
              <section className="space-y-6 rounded-2xl border p-6">
                <div>
                  <h2 className="text-lg font-semibold">Step 1 · Pickem details</h2>
                  <p className="text-sm text-muted-foreground">
                    Give the game a name and choose when it should start.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium">Game name</label>
                    <Field
                      name="gameName"
                      className="mt-1 w-full rounded-md border px-3 py-2"
                      placeholder="Premier League Saturday Pickem"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Start date and time</label>
                    <Field name="startUtc">
                      {({ field }: { field: { name: string; value: string } }) => (
                        <input
                          type="datetime-local"
                          value={toDateTimeLocalValue(field.value)}
                          onChange={e => {
                            const isoValue = fromDateTimeLocalValue(e.target.value);
                            setFieldValue(field.name, isoValue);
                          }}
                          className="mt-1 w-full rounded-md border px-3 py-2"
                        />
                      )}
                    </Field>
                    <p className="mt-1 text-xs text-muted-foreground">Stored as UTC internally.</p>
                  </div>
                </div>
              </section>
            ) : null}

            {step === 2 ? (
              <section className="space-y-6 rounded-2xl border p-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Step 2 · Fixtures</h2>
                    <p className="text-sm text-muted-foreground">
                      Confirm which selected fixtures should be included in this pickem.
                    </p>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {selectedFixtureIds.length} selected
                  </div>
                </div>

                <div className="flex flex-col gap-3 lg:flex-row">
                  <input
                    value={fixtureSearch}
                    onChange={e => setFixtureSearch(e.target.value)}
                    placeholder="Search fixtures"
                    className="w-full rounded-md border px-3 py-2"
                  />

                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-md border px-3 py-2 text-sm"
                      onClick={() => {
                        const nextIds = visibleEvents.map(event => event.providerEventId);
                        setSelectedFixtureIds(nextIds);
                      }}
                    >
                      Select visible
                    </button>

                    <button
                      type="button"
                      className="rounded-md border px-3 py-2 text-sm"
                      onClick={() => {
                        setSelectedFixtureIds([]);
                        syncQuestionsWithFixtures([]);
                      }}
                    >
                      Clear all
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {visibleEvents.length === 0 ? (
                    <div className="rounded-xl border p-4 text-sm text-muted-foreground">
                      No fixtures match your search.
                    </div>
                  ) : (
                    visibleEvents.map(event => {
                      const checked = selectedFixtureIds.includes(event.providerEventId);

                      return (
                        <label
                          key={event.providerEventId}
                          className="flex cursor-pointer items-start gap-3 rounded-xl border p-4"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const nextIds = e.target.checked
                                ? [...selectedFixtureIds, event.providerEventId]
                                : selectedFixtureIds.filter(id => id !== event.providerEventId);

                              setSelectedFixtureIds(nextIds);

                              if (!e.target.checked) {
                                syncQuestionsWithFixtures(nextIds);
                              }
                            }}
                            className="mt-1"
                          />

                          <div className="min-w-0">
                            <div className="font-medium">{event.name}</div>
                            <div className="text-sm text-muted-foreground">
                              Event ID: {event.providerEventId}
                            </div>
                            {'startUtc' in event &&
                            typeof event.startUtc === 'string' &&
                            event.startUtc ? (
                              <div className="text-sm text-muted-foreground">
                                Starts: {formatUtc(event.startUtc)}
                              </div>
                            ) : null}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </section>
            ) : null}

            {step === 3 ? (
              <section className="space-y-6">
                <div className="rounded-2xl border p-6">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">Step 3 · Questions</h2>
                      <p className="text-sm text-muted-foreground">
                        Choose the grouped markets you want to turn into pickem questions.
                      </p>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      {selectedQuestions.length} selected
                    </div>
                  </div>

                  <div className="mt-4">
                    <input
                      value={marketSearch}
                      onChange={e => setMarketSearch(e.target.value)}
                      placeholder="Search questions, fixtures, source markets or brands"
                      className="w-full rounded-md border px-3 py-2"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  {activeGroupedQuestions.map(group => {
                    const normalizedSearch = marketSearch.trim().toLowerCase();

                    const visibleQuestions = group.questions.filter(question => {
                      if (!normalizedSearch) return true;

                      const sourceMarketNames = question.sourceMarkets
                        .map(market => market.providerMarketName)
                        .join(' ')
                        .toLowerCase();

                      const brands = question.brands.join(' ').toLowerCase();

                      return (
                        question.questionText.toLowerCase().includes(normalizedSearch) ||
                        question.providerEventName.toLowerCase().includes(normalizedSearch) ||
                        sourceMarketNames.includes(normalizedSearch) ||
                        brands.includes(normalizedSearch)
                      );
                    });

                    if (!visibleQuestions.length) {
                      return null;
                    }

                    //const selectedVisibleCount = visibleQuestions.filter((q) => q.enabled).length;

                    const isOpen = openEventIds.includes(group.providerEventId);
                    const selectedCount = group.questions.filter(q => q.enabled).length;

                    return (
                      <div key={group.providerEventId} className="rounded-2xl border">
                        <div className="flex flex-col gap-3 border-b p-4 lg:flex-row lg:items-center lg:justify-between">
                          <button
                            type="button"
                            onClick={() => {
                              setOpenEventIds(current =>
                                current.includes(group.providerEventId)
                                  ? current.filter(id => id !== group.providerEventId)
                                  : [...current, group.providerEventId]
                              );
                            }}
                            className="text-left"
                          >
                            <div className="font-semibold">{group.providerEventName}</div>
                            <div className="text-sm text-muted-foreground">
                              {visibleQuestions.length} available • {selectedCount} selected
                            </div>
                          </button>

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded-md border px-3 py-1.5 text-sm"
                              onClick={() => {
                                visibleQuestions.forEach(question => {
                                  const questionIndex = values.questions.findIndex(
                                    q =>
                                      q.providerEventId === question.providerEventId &&
                                      q.questionGroupKey === question.questionGroupKey
                                  );

                                  if (questionIndex >= 0) {
                                    setFieldValue(
                                      `questions.${questionIndex}.enabled`,
                                      true,
                                      false
                                    );
                                  }
                                });
                              }}
                            >
                              Add all visible
                            </button>

                            <button
                              type="button"
                              className="rounded-md border px-3 py-1.5 text-sm"
                              onClick={() => {
                                visibleQuestions.forEach(question => {
                                  const questionIndex = values.questions.findIndex(
                                    q =>
                                      q.providerEventId === question.providerEventId &&
                                      q.questionGroupKey === question.questionGroupKey
                                  );

                                  if (questionIndex >= 0) {
                                    setFieldValue(
                                      `questions.${questionIndex}.enabled`,
                                      false,
                                      false
                                    );
                                  }
                                });
                              }}
                            >
                              Remove all visible
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setOpenEventIds(current =>
                                  current.includes(group.providerEventId)
                                    ? current.filter(id => id !== group.providerEventId)
                                    : [...current, group.providerEventId]
                                );
                              }}
                              className="rounded-md border px-3 py-1.5 text-sm"
                            >
                              {isOpen ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        </div>

                        {isOpen ? (
                          <div className="space-y-4 p-4">
                            {visibleQuestions.map(question => {
                              const questionIndex = values.questions.findIndex(
                                q =>
                                  q.providerEventId === question.providerEventId &&
                                  q.questionGroupKey === question.questionGroupKey
                              );

                              const questionValue = values.questions[questionIndex];
                              const previewOptions = question.options
                                .slice(0, 3)
                                .map(o => o.optionText)
                                .join(' / ');

                              return (
                                <div
                                  key={question.questionGroupKey}
                                  className="rounded-xl border p-4"
                                >
                                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="min-w-0">
                                      <div className="font-medium">
                                        {questionValue.questionText}
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        {question.options.length} options
                                        {previewOptions ? ` • ${previewOptions}` : ''}
                                      </div>
                                      <div className="mt-1 text-xs text-muted-foreground">
                                        Source markets:{' '}
                                        {question.sourceMarkets
                                          .map(market => market.brand)
                                          .join(' • ')}
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        setFieldValue(
                                          `questions.${questionIndex}.enabled`,
                                          !questionValue.enabled
                                        )
                                      }
                                      className="rounded-md border px-3 py-1.5 text-sm"
                                    >
                                      {questionValue.enabled ? 'Remove' : 'Add'}
                                    </button>
                                  </div>

                                  {questionValue.enabled ? (
                                    <div className="mt-4 space-y-4 border-t pt-4">
                                      <div>
                                        <label className="block text-sm font-medium">
                                          Question text
                                        </label>
                                        <Field
                                          name={`questions.${questionIndex}.questionText`}
                                          className="mt-1 w-full rounded-md border px-3 py-2"
                                        />
                                      </div>

                                      <div>
                                        <label className="block text-sm font-medium">
                                          Question type
                                        </label>
                                        <Field
                                          name={`questions.${questionIndex}.questionType`}
                                          as="select"
                                          className="mt-1 w-full rounded-md border px-3 py-2"
                                        >
                                          <option value="team">Team</option>
                                          <option value="player">Player</option>
                                          <option value="prop">Prop</option>
                                        </Field>
                                      </div>

                                      <details className="rounded-lg border">
                                        <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                                          Edit options
                                        </summary>

                                        <div className="space-y-3 border-t p-3">
                                          {questionValue.options.map((option, oi) => (
                                            <div
                                              key={option.logicalOptionKey}
                                              className="rounded-lg border p-3"
                                            >
                                              <label className="flex items-center gap-3">
                                                <Field
                                                  type="checkbox"
                                                  name={`questions.${questionIndex}.options.${oi}.enabled`}
                                                />
                                                <span>{option.optionText}</span>
                                              </label>

                                              <div className="mt-2">
                                                <label className="block text-sm font-medium">
                                                  Option text
                                                </label>
                                                <Field
                                                  name={`questions.${questionIndex}.options.${oi}.optionText`}
                                                  className="mt-1 w-full rounded-md border px-3 py-2"
                                                />
                                              </div>

                                              <details className="mt-3 rounded border">
                                                <summary className="cursor-pointer px-3 py-2 text-sm text-muted-foreground">
                                                  Show source details
                                                </summary>

                                                <div className="space-y-2 border-t p-3 text-xs text-muted-foreground">
                                                  <div>
                                                    {option.offers.length} brand offer
                                                    {option.offers.length === 1 ? '' : 's'}
                                                  </div>

                                                  {option.offers.map(offer => (
                                                    <div
                                                      key={`${offer.brand}-${offer.providerSelectionId}`}
                                                      className="rounded border px-2 py-1"
                                                    >
                                                      {offer.brand} • Selection ID:{' '}
                                                      {offer.providerSelectionId}
                                                      {offer.oddsDecimal != null
                                                        ? ` • Odds ${offer.oddsDecimal}`
                                                        : ''}
                                                      <div className="mt-0.5">
                                                        Market: {offer.providerMarketName}
                                                      </div>
                                                      {offer.selectionName !== option.optionText ? (
                                                        <div className="mt-0.5">
                                                          Feed label: {offer.selectionName}
                                                        </div>
                                                      ) : null}
                                                    </div>
                                                  ))}
                                                </div>
                                              </details>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}

                  {activeGroupedQuestions.length === 0 ? (
                    <div className="rounded-xl border p-4 text-sm text-muted-foreground">
                      No grouped questions are available for the selected fixtures.
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {step === 4 ? (
              <section className="space-y-6 rounded-2xl border p-6">
                <div>
                  <h2 className="text-lg font-semibold">Step 4 · Review</h2>
                  <p className="text-sm text-muted-foreground">
                    Check the details below before creating the pickem.
                  </p>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-xl border p-4">
                    <h3 className="font-semibold">Pickem details</h3>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div>
                        <dt className="text-muted-foreground">Game name</dt>
                        <dd>{values.gameName || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Start</dt>
                        <dd>{values.startUtc ? formatUtc(values.startUtc) : '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">League</dt>
                        <dd>{leagueCode}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="rounded-xl border p-4">
                    <h3 className="font-semibold">Summary</h3>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div>
                        <dt className="text-muted-foreground">Fixtures selected</dt>
                        <dd>{selectedFixtureIds.length}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Questions selected</dt>
                        <dd>{selectedQuestions.length}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Invalid questions</dt>
                        <dd>{invalidSelectedQuestions.length}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="rounded-xl border p-4">
                  <h3 className="font-semibold">Fixtures</h3>
                  <div className="mt-3 space-y-2">
                    {availableEvents
                      .filter(event => selectedFixtureIds.includes(event.providerEventId))
                      .map(event => (
                        <div key={event.providerEventId} className="rounded border px-3 py-2">
                          <div className="font-medium">{event.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Event ID: {event.providerEventId}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="rounded-xl border p-4">
                  <h3 className="font-semibold">Questions</h3>

                  {reviewQuestionsByEvent.length === 0 ? (
                    <div className="mt-3 text-sm text-muted-foreground">
                      No questions selected yet.
                    </div>
                  ) : (
                    <div className="mt-3 space-y-4">
                      {reviewQuestionsByEvent.map(group => (
                        <div key={group.providerEventId} className="rounded-lg border p-3">
                          <div className="font-medium">{group.providerEventName}</div>
                          <div className="mt-2 space-y-2">
                            {group.questions.map(question => {
                              const enabledOptions = question.options.filter(o => o.enabled);

                              return (
                                <div
                                  key={question.questionGroupKey}
                                  className="rounded border px-3 py-2"
                                >
                                  <div className="font-medium">{question.questionText}</div>
                                  <div className="text-sm text-muted-foreground">
                                    Type: {question.questionType} • {enabledOptions.length} enabled
                                    options
                                  </div>
                                  <div className="mt-1 text-xs text-muted-foreground">
                                    {enabledOptions.map(option => option.optionText).join(' • ')}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {invalidSelectedQuestions.length > 0 ? (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Some selected questions are invalid. Each selected question needs question text
                    and at least two enabled options.
                  </div>
                ) : null}
              </section>
            ) : null}

            <footer className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">Step {step} of 4</div>

              <div className="flex flex-wrap gap-2">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={() => setStep(current => (current - 1) as BuilderStep)}
                    className="rounded-md border px-4 py-2"
                  >
                    Back
                  </button>
                ) : null}

                {step < 4 ? (
                  <button
                    type="button"
                    onClick={() => goToStep((step + 1) as BuilderStep)}
                    className="rounded-md border px-4 py-2"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={submitForm}
                    className="rounded-md border px-4 py-2 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Creating…' : 'Create pickem'}
                  </button>
                )}
              </div>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
}

function StepIndicator({ currentStep }: { currentStep: BuilderStep }) {
  const steps = [
    { step: 1, label: 'Details' },
    { step: 2, label: 'Fixtures' },
    { step: 3, label: 'Questions' },
    { step: 4, label: 'Review' },
  ] as const;

  return (
    <div className="grid gap-3 sm:grid-cols-4">
      {steps.map(item => {
        const isActive = item.step === currentStep;
        const isComplete = item.step < currentStep;

        return (
          <div
            key={item.step}
            className={[
              'rounded-xl border px-3 py-3 text-sm',
              isActive ? 'border-foreground' : '',
              isComplete ? 'bg-muted/40' : '',
            ].join(' ')}
          >
            <div className="text-xs text-muted-foreground">Step {item.step}</div>
            <div className="font-medium">{item.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function groupQuestionsByEvent(questions: FlutterBuilderValues['questions']) {
  const groups = new Map<
    string,
    {
      providerEventId: string;
      providerEventName: string;
      questions: FlutterBuilderValues['questions'];
    }
  >();

  for (const question of questions) {
    const existing = groups.get(question.providerEventId);

    if (existing) {
      existing.questions.push(question);
      continue;
    }

    groups.set(question.providerEventId, {
      providerEventId: question.providerEventId,
      providerEventName: question.providerEventName,
      questions: [question],
    });
  }

  return Array.from(groups.values());
}

function toDateTimeLocalValue(isoUtc: string) {
  if (!isoUtc) return '';

  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number) => String(value).padStart(2, '0');

  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1);
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function fromDateTimeLocalValue(value: string) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toISOString();
}

function formatUtc(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return (
    new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
    }).format(date) + ' UTC'
  );
}
